/**
 * feat-302 — View as: filter the LOADED tree to the id sets the backend
 * returned (ADR §10.4). Id-set membership only — never a re-evaluation of the
 * scope rule (spec "View as": no TypeScript twin).
 *
 * PURE. Lesson numbers keep their full-curriculum values (NOTES §6.5), and a
 * multi-Track Lesson is rendered in full under the FIRST kept Track that lists
 * it — so when its owner Track is filtered away, a former stub becomes the full
 * rendering rather than a pointer to nothing.
 */
import { GroupedCurriculum, LessonGroup, LessonPlacement } from '../groupByTrack';

export interface ViewAsKeep {
  trackIds: ReadonlySet<string>;
  lessonIds: ReadonlySet<string>;
}

export function filterGrouped(grouped: GroupedCurriculum, keep: ViewAsKeep): GroupedCurriculum {
  if (grouped.mode === 'flat') {
    const lessons: typeof grouped.lessons = [];
    const numbers: number[] = [];
    grouped.lessons.forEach((u, i) => {
      if (!keep.lessonIds.has(u.id)) return;
      lessons.push(u);
      numbers.push(grouped.numbers?.[i] ?? i + 1);
    });
    return { mode: 'flat', lessons, numbers };
  }

  const owner = new Map<string, LessonGroup>();
  const groups: LessonGroup[] = [];
  for (const g of grouped.groups) {
    if (g.kind === 'track' && !keep.trackIds.has(g.id)) continue;
    const next: LessonGroup = { ...g, entries: [] };
    for (const e of g.entries) {
      if (!keep.lessonIds.has(e.lesson.id)) continue;
      const first = owner.get(e.lesson.id);
      let placed: LessonPlacement;
      if (!first) {
        owner.set(e.lesson.id, next);
        placed = { kind: 'full', lesson: e.lesson, number: e.number };
      } else {
        placed = {
          kind: 'stub',
          lesson: e.lesson,
          number: e.number,
          ownerGroupId: first.id,
          ownerTitle: first.title,
        };
      }
      next.entries.push(placed);
    }
    // The "Not in any Track" bucket is not a Track: it shows only while it
    // still holds something. A kept Track stays even when empty for the view.
    if (g.kind === 'unassigned' && next.entries.length === 0) continue;
    groups.push(next);
  }
  return { mode: 'tracks', groups };
}

/** True when the filtered tree has nothing to render (the empty-result card). */
export function isEmptyView(grouped: GroupedCurriculum): boolean {
  return grouped.mode === 'flat' ? grouped.lessons.length === 0 : grouped.groups.length === 0;
}
